import React, { useState, useRef, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import style from "./Navbar.module.css"
import logo from "/mm_logo.png"
import Search from "../../icons/Search"
import Account from "../../icons/Account"
import Cart from "../../icons/Cart"
import { useDispatch, useSelector } from "react-redux"
import { updateSearch } from "../../data/GlobalSlice"
import { openCartSidebar } from "../../data/CartSlice"
import { useAuth } from "../../data/useAuth"
import Logout from "../../icons/Logout"
import DashboardIcon from "../../icons/DashboardIcon"
import ShoppingIcon from "../../icons/ShoppingIcon"
import useHasPermission from "../hook/useHasPermisison"
import CategoryIcon from "../../icons/CategoryIcon"
import OrderIcon from "../../icons/OrderIcon"
import HomeIcon from "../../icons/HomeIcon"
import TruckIcon from "../../icons/TruckIcon"
import EmployeeIcon from "../../icons/EmployeeIcon"
import DollarIcon from "../../icons/DollerIcon"

const Navbar = ({ current = "", is_admin = false, searchDisabled = false }) => {
  const dispatch = useDispatch()
  const cartItemsCount = useSelector((state) =>
    state.cart.items.reduce(
      (acc, item) => acc + (parseInt(item.qty, 10) || 0),
      0
    )
  )
  const { isLoggedIn, user, signOut } = useAuth()
  const navigate = useNavigate()
  const [showAccountMenu, setShowAccountMenu] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const menuRef = useRef(null)

  // Permissions for Admin Sidebar
  const productPermission = useHasPermission(user?.permissions, [
    "product_manager",
    "super_user",
  ])
  const categoryPermission = useHasPermission(user?.permissions, [
    "category_manager",
    "super_user",
  ])
  const deliveryPermission = useHasPermission(user?.permissions, [
    "delivery_manager",
    "super_user",
  ])
  const chargePermission = useHasPermission(user?.permissions, [
    "charge_manager",
    "super_user",
  ])
  const employeePermission = useHasPermission(user?.permissions, [
    "hr",
    "super_user",
  ])
  const adminOrder = useHasPermission(user?.permissions, ["super_user"])

  // Close account dropdown and lock scroll when mobile menu is open
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowAccountMenu(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "auto"
    }
    return () => {
      document.body.style.overflow = "auto"
    }
  }, [mobileMenuOpen])



  return (
    <>
      <nav
        className={`${style.nav_outer} ${
          is_admin ? style.nav_admin : style.nav_normal
        }`}
      >
        {is_admin ? (
          <ul>
            <li>
              <Link to={"/"} className={style.logoLink}>
                <img src={logo} alt="Dr. Malu Mahendran Logo" />
              </Link>
            </li>

            <li className={style.nav_admin_links_heading}>{current}</li>
            <div className={style.nav_right_actions}>
              <li>
                <button
                  type="button"
                  className={style.userBtn}
                  onClick={() => {
                    if (!isLoggedIn) {
                      navigate("/login")
                    }
                  }}
                >
                  <Account />
                  <span>{isLoggedIn ? user?.first_name : "Login"}</span>
                </button>
              </li>
              <button
                type="button"
                className={style.hamburgerBtn}
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                aria-label="Toggle admin menu"
              >
                {mobileMenuOpen ? (
                  <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2.5" fill="none">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2.5" fill="none">
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </svg>
                )}
              </button>
            </div>
          </ul>
        ) : (
          <ul>
            <li>
              <Link to={"/"} className={style.logoLink}>
                <img src={logo} alt="Dr. Malu Mahendran Logo" />
              </Link>
            </li>

            {/* Search Input Bar */}
            <li className={style.search_bar}>
              {!searchDisabled && (
                <>
                  <input
                    type="search"
                    placeholder="Search products..."
                    onChange={(e) => {
                      dispatch(updateSearch(e.target.value))
                    }}
                  />
                  <Search />
                </>
              )}
            </li>

            {/* Desktop Action Links & Hamburger Button */}
            <div className={style.nav_right_actions}>
              <ul className={style.nav_links_list}>
                <li>
                  <Link to={"/"} title="Home">
                    <HomeIcon />
                  </Link>
                </li>

                {/* Cart Trigger */}
                <li>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault()
                      if (isLoggedIn) {
                        dispatch(openCartSidebar())
                      } else {
                        navigate("/login")
                      }
                    }}
                    className={style.cartIconWrapper}
                    title="Shopping Cart"
                  >
                    <Cart />
                    {isLoggedIn && cartItemsCount > 0 && (
                      <span className={style.cartBadge}>{cartItemsCount}</span>
                    )}
                  </Link>
                </li>

                {/* Account Dropdown Trigger */}
                <li className={style.nav_account} ref={menuRef}>
                  {isLoggedIn ? (
                    <button
                      type="button"
                      className={style.userBtn}
                      onClick={() => setShowAccountMenu((prev) => !prev)}
                    >
                      <Account />
                      <span>{user?.first_name || "Account"}</span>
                    </button>
                  ) : (
                    <Link to="/login" className={style.userBtn}>
                      <Account />
                      <span>Login</span>
                    </Link>
                  )}

                  {isLoggedIn && showAccountMenu && (
                    <div className={style.accountMenu}>
                      <Link
                        to="/account"
                        className={style.accountMenuItem}
                        onClick={() => setShowAccountMenu(false)}
                      >
                        <Account /> My Account
                      </Link>
                      <Link
                        to="/orders"
                        className={style.accountMenuItem}
                        onClick={() => setShowAccountMenu(false)}
                      >
                        <OrderIcon /> My Orders
                      </Link>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAccountMenu(false)
                          signOut()
                        }}
                        className={`${style.accountMenuItem} ${style.logoutItem}`}
                        style={{
                          background: "none",
                          border: "none",
                          width: "100%",
                          textAlign: "left",
                        }}
                      >
                        <Logout /> Logout
                      </button>
                    </div>
                  )}
                </li>
              </ul>

              {/* Hamburger Button for Mobile View */}
              <button
                type="button"
                className={style.hamburgerBtn}
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                aria-label="Toggle navigation menu"
              >
                {mobileMenuOpen ? (
                  <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2.5" fill="none">
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="24" height="24" stroke="currentColor" strokeWidth="2.5" fill="none">
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </svg>
                )}
              </button>
            </div>
          </ul>
        )}

        {/* Mobile Search Bar */}
        {!searchDisabled && !is_admin && (
          <div className={style.mobile_search}>
            <div>
              <input
                type="search"
                placeholder="Search products..."
                onChange={(e) => {
                  dispatch(updateSearch(e.target.value))
                }}
              />
              <Search />
            </div>
          </div>
        )}

        {/* Horizontal Mobile Admin Sub-Nav */}
        {is_admin && (
          <div className={style.mobileAdminSubNav}>
            <Link className={current === "Dashboard" ? style.activeSub : ""} to="/" onClick={() => setMobileMenuOpen(false)}>Dashboard</Link>
            {employeePermission && <Link className={current === "Employee" ? style.activeSub : ""} to="/employee" onClick={() => setMobileMenuOpen(false)}>Employee</Link>}
            {adminOrder && <Link className={current === "Orders" ? style.activeSub : ""} to="/orders" onClick={() => setMobileMenuOpen(false)}>Orders</Link>}
            {categoryPermission && <Link className={current === "Category" ? style.activeSub : ""} to="/category" onClick={() => setMobileMenuOpen(false)}>Category</Link>}
            {productPermission && <Link className={current === "Product" ? style.activeSub : ""} to="/product" onClick={() => setMobileMenuOpen(false)}>Product</Link>}
            {deliveryPermission && <Link className={current === "Delivery" ? style.activeSub : ""} to="/delivery" onClick={() => setMobileMenuOpen(false)}>Delivery</Link>}
            {chargePermission && <Link className={current === "Charge" ? style.activeSub : ""} to="/charge" onClick={() => setMobileMenuOpen(false)}>Charge</Link>}
          </div>
        )}

        {/* Mobile Slide-out Drawer Overlay */}
        {mobileMenuOpen && (
          <div className={style.mobileDrawerOverlay} onClick={() => setMobileMenuOpen(false)}>
            <div className={style.mobileDrawer} onClick={(e) => e.stopPropagation()}>
              <div className={style.mobileDrawerHeader}>
                <div className={style.drawerUserTitle}>
                  {is_admin ? "Admin Portal" : (isLoggedIn ? `Hello, ${user?.first_name || "User"}` : "Welcome")}
                </div>
                <button
                  type="button"
                  className={style.closeDrawerBtn}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  &times;
                </button>
              </div>

              <div className={style.mobileDrawerNav}>
                {is_admin ? (
                  <>
                    <Link className={current === "Dashboard" ? style.activeDrawer : ""} to="/" onClick={() => setMobileMenuOpen(false)}>
                      <DashboardIcon /> <span>Dashboard</span>
                    </Link>
                    {employeePermission && (
                      <Link className={current === "Employee" ? style.activeDrawer : ""} to="/employee" onClick={() => setMobileMenuOpen(false)}>
                        <EmployeeIcon /> <span>Employee</span>
                      </Link>
                    )}
                    {adminOrder && (
                      <Link className={current === "Orders" ? style.activeDrawer : ""} to="/orders" onClick={() => setMobileMenuOpen(false)}>
                        <OrderIcon /> <span>Orders</span>
                      </Link>
                    )}
                    {categoryPermission && (
                      <Link className={current === "Category" ? style.activeDrawer : ""} to="/category" onClick={() => setMobileMenuOpen(false)}>
                        <CategoryIcon /> <span>Category</span>
                      </Link>
                    )}
                    {productPermission && (
                      <Link className={current === "Product" ? style.activeDrawer : ""} to="/product" onClick={() => setMobileMenuOpen(false)}>
                        <ShoppingIcon /> <span>Product</span>
                      </Link>
                    )}
                    {deliveryPermission && (
                      <Link className={current === "Delivery" ? style.activeDrawer : ""} to="/delivery" onClick={() => setMobileMenuOpen(false)}>
                        <TruckIcon /> <span>Delivery State</span>
                      </Link>
                    )}
                    {chargePermission && (
                      <Link className={current === "Charge" ? style.activeDrawer : ""} to="/charge" onClick={() => setMobileMenuOpen(false)}>
                        <DollarIcon /> <span>Delivery Charge</span>
                      </Link>
                    )}
                    <button
                      type="button"
                      className={style.mobileDrawerLogout}
                      onClick={() => {
                        setMobileMenuOpen(false)
                        signOut()
                      }}
                    >
                      <Logout /> <span>Logout</span>
                    </button>
                  </>
                ) : (
                  <>
                    <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                      <HomeIcon /> <span>Home</span>
                    </Link>

                    <Link
                      to="#"
                      onClick={(e) => {
                        e.preventDefault()
                        setMobileMenuOpen(false)
                        if (isLoggedIn) {
                          dispatch(openCartSidebar())
                        } else {
                          navigate("/login")
                        }
                      }}
                    >
                      <Cart /> <span>Shopping Cart {isLoggedIn && cartItemsCount > 0 ? `(${cartItemsCount})` : ""}</span>
                    </Link>

                    {isLoggedIn ? (
                      <>
                        <Link to="/account" onClick={() => setMobileMenuOpen(false)}>
                          <Account /> <span>My Account</span>
                        </Link>
                        <Link to="/orders" onClick={() => setMobileMenuOpen(false)}>
                          <OrderIcon /> <span>My Orders</span>
                        </Link>
                        <button
                          type="button"
                          className={style.mobileDrawerLogout}
                          onClick={() => {
                            setMobileMenuOpen(false)
                            signOut()
                          }}
                        >
                          <Logout /> <span>Logout</span>
                        </button>
                      </>
                    ) : (
                      <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                        <Account /> <span>Login / Sign Up</span>
                      </Link>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Admin Sidebar Links */}
      {is_admin && (
        <div className={style.nav_admin_links}>
          <ul>
            {isLoggedIn && (
              <>
                <li>
                  <Link
                    className={current === "Dashboard" ? style.active : ""}
                    to={"/"}
                  >
                    <DashboardIcon /> <span>Dashboard</span>
                  </Link>
                </li>
                {employeePermission && (
                  <li>
                    <Link
                      className={current === "Employee" ? style.active : ""}
                      to={"/employee"}
                    >
                      <EmployeeIcon /> <span>Employee</span>
                    </Link>
                  </li>
                )}
                {adminOrder && (
                  <li>
                    <Link
                      className={current === "Orders" ? style.active : ""}
                      to={"/orders"}
                    >
                      <OrderIcon /> <span>Orders</span>
                    </Link>
                  </li>
                )}
                {categoryPermission && (
                  <li>
                    <Link
                      className={current === "Category" ? style.active : ""}
                      to={"/category"}
                    >
                      <CategoryIcon /> <span>Category</span>
                    </Link>
                  </li>
                )}
                {productPermission && (
                  <li>
                    <Link
                      className={current === "Product" ? style.active : ""}
                      to={"/product"}
                    >
                      <ShoppingIcon /> <span>Product</span>
                    </Link>
                  </li>
                )}
                {deliveryPermission && (
                  <li>
                    <Link
                      className={current === "Delivery" ? style.active : ""}
                      to={"/delivery"}
                    >
                      <TruckIcon /> <span>Delivery</span>
                    </Link>
                  </li>
                )}
                {chargePermission && (
                  <li>
                    <Link
                      className={current === "Charge" ? style.active : ""}
                      to={"/charge"}
                    >
                      <DollarIcon /> <span>Charge</span>
                    </Link>
                  </li>
                )}
                <li>
                  <Link
                    to="#"
                    onClick={(e) => {
                      e.preventDefault()
                      signOut()
                    }}
                  >
                    <Logout /> <span>Logout</span>
                  </Link>
                </li>
              </>
            )}
          </ul>
        </div>
      )}
    </>
  )
}

export default Navbar
